import * as Sentry from '@sentry/react';
import { readQuestionnairePolicyEvaluation, type QuestionnairePolicyQuestionIdentity } from './questionnairePolicy';

interface PolicyRpcFailure {
  message?: string;
  code?: string | null;
  details?: string | null;
  hint?: string | null;
}

export class QuestionnairePolicyRpcError extends Error {
  constructor(readonly rpcName: string, readonly failure: PolicyRpcFailure) {
    super(failure.message || 'questionnaire_policy_request_failed');
    this.name = 'QuestionnairePolicyRpcError';
  }
}

/** Capture once at the RPC boundary; answers and supplier notes never enter telemetry. */
export async function executeQuestionnairePolicyRpc<T>(
  rpcName: string,
  request: () => PromiseLike<{ data: unknown; error: PolicyRpcFailure | null }>,
  readResult: (data: unknown) => T,
) {
  try {
    const { data, error } = await request();
    if (error) throw new QuestionnairePolicyRpcError(rpcName, error);
    return readResult(data);
  } catch (cause) {
    const error = cause instanceof QuestionnairePolicyRpcError ? cause
      : new QuestionnairePolicyRpcError(rpcName, {
        message: cause instanceof Error ? cause.message : 'questionnaire_policy_request_failed',
      });
    const { code, details, hint } = error.failure;
    const message = error.message;
    const errorCode = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/.test(message)
      ? message : code ? `sqlstate_${code}` : 'questionnaire_policy_request_failed';
    const context = {
      fingerprint: ['questionnaire_policy', rpcName, errorCode],
      tags: { feature: 'questionnaire_policy', rpc_name: rpcName, error_code: errorCode,
        route: typeof window === 'undefined' ? 'unknown' : window.location.pathname },
      extra: { rpcName, errorCode, sqlState: code ?? null, details: details ?? null, hint: hint ?? null },
    };
    if (code === '22023' || code === '42501') {
      Sentry.captureMessage(`Questionnaire policy blocked by ${rpcName}: ${errorCode}`, { ...context, level: 'warning' });
    } else {
      Sentry.captureException(Object.assign(new Error(`Questionnaire policy failed in ${rpcName}: ${message}`), { cause: error }), {
        ...context, level: 'error',
      });
    }
    throw error;
  }
}

export function evaluateQuestionnairePolicyRpc(
  rpcName: string,
  request: () => PromiseLike<{ data: unknown; error: PolicyRpcFailure | null }>,
  expectedQuestions?: readonly QuestionnairePolicyQuestionIdentity[],
) {
  return executeQuestionnairePolicyRpc(rpcName, request, (data) => readQuestionnairePolicyEvaluation(data, expectedQuestions));
}
